import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Button, Typography } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'

import { SubscriptionPlan, TrialPeriod } from '@vacationtracker/shared/types/company'
import { SubscriptionPeriod } from '@vacationtracker/shared/types/billing'
import { StripePaymentStatus } from '@vacationtracker/shared/types/stripe'

import IntlMessages from '../../util/IntlMessages'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { SETUP_CALL_CALENDLY_URL } from '../../constants'
import { isEligibleForCompleteTrialRequest } from './constants'
import { isCompleteTrialRequested } from './request-storage'
import TryCompleteRequestModal from './request-modal'

import './styles.less'

const { Text } = Typography

export interface ITryCompleteRequestCtaProps {
  currentPlan: SubscriptionPlan
  currentPeriod: SubscriptionPeriod
  trialPeriod?: TrialPeriod
  activeUsers: number
  stripePaymentStatus?: StripePaymentStatus
  subscriptionCancelAtPeriodEnd?: boolean
  upcomingInvoiceInfo?: ReactNode
}

const TryCompleteRequestCta = ({
  currentPlan,
  currentPeriod,
  trialPeriod,
  activeUsers,
  stripePaymentStatus,
  subscriptionCancelAtPeriodEnd,
  upcomingInvoiceInfo,
}: ITryCompleteRequestCtaProps): React.ReactElement | null => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRequested, setIsRequested] = useState(() => isCompleteTrialRequested(authCompany?.id))
  const linkShownTracked = useRef(false)

  const isEligible = isEligibleForCompleteTrialRequest({
    plan: currentPlan,
    trialPeriod,
    stripePaymentStatus,
    subscriptionCancelAtPeriodEnd,
  })

  useEffect(() => {
    if (!isEligible || isRequested || linkShownTracked.current) {
      return
    }
    linkShownTracked.current = true
    track('COMPLETE_TRIAL_LINK_SHOWN', getAnalyticsProperties({ activeUsers, stripePaymentStatus }))
  }, [isEligible, isRequested])

  if (!isEligible) {
    return null
  }

  if (isRequested) {
    return (
      <div className="try-complete-request-received">
        <CheckCircleOutlined />
        <div>
          <Text strong>
            <IntlMessages id="completeTrialRequest.received.title" />
          </Text>
          <div>
            <Text type="secondary">
              <IntlMessages id="completeTrialRequest.received.description" />
            </Text>{' '}
            <a
              href={SETUP_CALL_CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track('COMPLETE_TRIAL_BOOKING_CLICKED', {
                  ...getAnalyticsProperties({ activeUsers, stripePaymentStatus }),
                  source: 'billing_page',
                })
              }
            >
              <IntlMessages id="completeTrialRequest.bookACall" />
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="try-complete-request-link">
      <Button
        type="link"
        onClick={() => {
          track('COMPLETE_TRIAL_LINK_CLICKED', getAnalyticsProperties({ activeUsers, stripePaymentStatus }))
          setIsModalOpen(true)
        }}
      >
        <IntlMessages id="completeTrialRequest.link" />
      </Button>
      {isModalOpen && (
        <TryCompleteRequestModal
          onCancel={() => setIsModalOpen(false)}
          onSubmitted={() => {
            setIsModalOpen(false)
            setIsRequested(true)
          }}
          currentPlan={currentPlan}
          currentPeriod={currentPeriod}
          activeUsers={activeUsers}
          stripePaymentStatus={stripePaymentStatus}
          upcomingInvoiceInfo={upcomingInvoiceInfo}
        />
      )}
    </div>
  )
}

export default TryCompleteRequestCta
